// src/middlewares/authRefresh.midware.js
import config from "config";

import { SILENT_REDIRECT_URI } from "../authConfig";
import { getIsCypressAut } from "../helpers/isCypressAut";
import { getSessionMsalAccount } from "../helpers/msalSession";
import { getAccessTokenFromStorage, tryParseJwt } from "../helpers/token";
import msalInstance, { msalReady } from "../msalInstance";

const { adsMsalConfig, emailMsalConfig } = config;

let refreshTimerId = null;
let started = false;

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000; // check every minute

const buildSilentRequestForAccount = (account, forceRefresh) => {
  const rawClaim =
    (account?.idTokenClaims &&
      (account.idTokenClaims.tfp || account.idTokenClaims.acr)) ||
    "";
  const isEmailPolicy =
    typeof rawClaim === "string" && /email/i.test(String(rawClaim));
  const authority = isEmailPolicy
    ? emailMsalConfig?.authority || adsMsalConfig.authority
    : adsMsalConfig.authority;
  return {
    scopes: ["openid", ...adsMsalConfig.b2cScopes],
    account,
    loginHint: account?.username,
    authority,
    forceRefresh: Boolean(forceRefresh),
    redirectUri: SILENT_REDIRECT_URI,
  };
};

const startRefreshLoop = () => {
  if (started) return;
  started = true;

  const loop = async () => {
    try {
      await msalReady;
      // E2E uses ROPC tokens; silent iframe refresh always hits AADB2C90077 and
      // spams InteractionRequiredAuthError in the console / error overlay.
      if (getIsCypressAut()) {
        return;
      }
      const active = getSessionMsalAccount(msalInstance);
      if (!active) return;

      const bearer = getAccessTokenFromStorage();
      const payload = tryParseJwt(bearer);
      const expMs = payload?.exp ? payload.exp * 1000 : null;
      const now = Date.now();
      const expiringSoon = expMs ? expMs - now <= FIVE_MINUTES_MS : true;

      if (expiringSoon) {
        const request = buildSilentRequestForAccount(active, false);
        try {
          await msalInstance.acquireTokenSilent(request);
        } catch (_e) {
          // Ignore; next request path will handle renew or user interaction
        }
      }
    } catch (_e) {
      // ignore init errors
    }
  };

  // run immediately and then at interval
  loop();
  refreshTimerId = setInterval(loop, CHECK_INTERVAL_MS);
};

export default function authRefreshMiddleware() {
  return () => (next) => (action) => {
    // fire-and-forget refresh loop once per app load
    if (!refreshTimerId) {
      startRefreshLoop();
    }
    return next(action);
  };
}
